import { Button, Checkbox, type CheckboxProps, Typography } from "@may-i/mash-design-system";
import * as RadixPopover from "@radix-ui/react-popover";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface AddAdjustGuideFilter {
  includesNotChanged: boolean;
  includesAdjusted: boolean;
  includesAdded: boolean;
}

interface Props {
  filter: AddAdjustGuideFilter;
  onApply: (next: AddAdjustGuideFilter) => void;
}

export function AddAdjustGuideCameraFilter({ filter, onApply }: Props) {
  const { t } = useTranslation("shared");
  const [isOpen, setIsOpen] = useState(false);
  const [localNotChanged, setLocalNotChanged] = useState(filter.includesNotChanged);
  const [localAdjusted, setLocalAdjusted] = useState(filter.includesAdjusted);
  const [localAdded, setLocalAdded] = useState(filter.includesAdded);

  const sync = () => {
    setLocalNotChanged(filter.includesNotChanged);
    setLocalAdjusted(filter.includesAdjusted);
    setLocalAdded(filter.includesAdded);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    sync();
  };

  const handleNotChangedChange = (checked: CheckboxProps["checked"]) => {
    if (typeof checked === "boolean") setLocalNotChanged(checked);
  };
  const handleChangedChange = (checked: CheckboxProps["checked"]) => {
    if (typeof checked === "boolean") {
      setLocalAdjusted(checked);
      setLocalAdded(checked);
    }
  };
  const handleAdjustedChange = (checked: CheckboxProps["checked"]) => {
    if (typeof checked === "boolean") setLocalAdjusted(checked);
  };
  const handleAddedChange = (checked: CheckboxProps["checked"]) => {
    if (typeof checked === "boolean") setLocalAdded(checked);
  };

  const handleClose = () => {
    setIsOpen(false);
    sync();
  };
  const handleApply = () => {
    setIsOpen(false);
    onApply({
      includesNotChanged: localNotChanged,
      includesAdjusted: localAdjusted,
      includesAdded: localAdded,
    });
  };

  const changedCheckboxValue: CheckboxProps["checked"] =
    localAdjusted && localAdded ? true : localAdjusted !== localAdded ? "indeterminate" : false;

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <FilterButton variant="text" color="primary" size="small" startIcon="ic_filter">
          {t("addAdjustGuideModal.addAdjustGuideCameraFilter.title.textContent")}
        </FilterButton>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <PopoverContent sideOffset={4} align="end">
          <Content>
            <OptionItem>
              <Typography typo="subheading2" color="colors.brand.primary">
                {t("addAdjustGuideModal.addAdjustGuideCameraFilter.optionItem.cameraChangeStatus.title.textContent")}
              </Typography>
              <CheckboxWrapper>
                <StyledCheckbox
                  label={t("addAdjustGuideModal.addAdjustGuideCameraFilter.optionItem.cameraChangeStatus.checkbox.notChanged.label")}
                  checked={localNotChanged}
                  onCheckedChange={handleNotChangedChange}
                  color="primary"
                />
                <StyledCheckbox
                  label={t("addAdjustGuideModal.addAdjustGuideCameraFilter.optionItem.cameraChangeStatus.checkbox.changed.label")}
                  checked={changedCheckboxValue}
                  onCheckedChange={handleChangedChange}
                  color="primary"
                />
                <StyledCheckbox
                  label={t("addAdjustGuideModal.addAdjustGuideCameraFilter.optionItem.cameraChangeStatus.checkbox.adjusted.label")}
                  checked={localAdjusted}
                  onCheckedChange={handleAdjustedChange}
                  color="primary"
                  $isSubItem
                />
                <StyledCheckbox
                  label={t("addAdjustGuideModal.addAdjustGuideCameraFilter.optionItem.cameraChangeStatus.checkbox.added.label")}
                  checked={localAdded}
                  onCheckedChange={handleAddedChange}
                  color="primary"
                  $isSubItem
                />
              </CheckboxWrapper>
            </OptionItem>
          </Content>
          <Footer>
            <Button variant="outlined" size="small" onClick={handleClose}>
              {t("addAdjustGuideModal.addAdjustGuideCameraFilter.closeButton.textContent")}
            </Button>
            <Button size="small" onClick={handleApply}>
              {t("addAdjustGuideModal.addAdjustGuideCameraFilter.applyButton.textContent")}
            </Button>
          </Footer>
        </PopoverContent>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

const FilterButton = styled(Button)`
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PopoverContent = styled(RadixPopover.Content)`
  z-index: 1000;
  background: #fff;
  border: 1px solid #E6E8F0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 200px;
`;

const Content = styled.main`
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 10px;
`;

const OptionItem = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 160px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

const StyledCheckbox = styled(Checkbox).attrs({ size: "medium" })<{ $isSubItem?: boolean }>`
  height: 32px;
  margin-left: ${({ $isSubItem }) => ($isSubItem ? "24px" : "0")};
`;

const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding: 0 16px 16px;
`;
